import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Zap } from 'react-feather';

import { Badge, Button, Callout, Card, Drawer, Loader, StatusBadge } from '../../../ui';
import {
  CandidateDetail,
  DrawerTab,
  FUNNEL,
  Genre,
  HiringMeta,
  SOURCE_LABELS,
  STAGE_LABELS,
  STAGE_STATUS_LABELS,
  Stage,
  errorText,
  getCandidate,
  initials,
  nextAction,
  when,
} from '../../../TeacherHiring/hiringApi';
import { AcademicPanel, AiInterviewPanel, Messages, ProfileCard, ScreeningPanel, Timeline } from './CandidateDetail';
import CopyLinkButton from './CopyLinkButton';
import MoveDialog from './MoveDialog';

const TABS: Array<{ key: DrawerTab; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'call', label: 'AI Call' },
  { key: 'interview', label: 'Video Interview' },
  { key: 'academic', label: 'Academic' },
  { key: 'timeline', label: 'Timeline' },
];

/** The journey as the mockup's Progress list: lead created, then each funnel stage. */
const PROGRESS: Array<{ stage: Stage; label: string }> = [
  { stage: 'SCREENING', label: 'AI Screening Call' },
  { stage: 'INTERVIEW_1', label: 'Video Interview (recorded on camera)' },
  { stage: 'INTERVIEW_2', label: 'Academic Interview' },
  { stage: 'TRAINING', label: 'Training & Onboarding' },
  { stage: 'CONTRACT', label: 'Contract Signing' },
  { stage: 'ONBOARDED', label: 'Ready for Teaching' },
];

const Progress: React.FC<{ detail: CandidateDetail }> = ({ detail }) => {
  const reachedAt = new Map<Stage, string>();
  // Events are newest first; walk oldest first so the first arrival wins.
  [...detail.events].reverse().forEach((e) => {
    if (e.type === 'STAGE_CHANGED' && e.toStage && !reachedAt.has(e.toStage)) reachedAt.set(e.toStage, e.createdAt);
  });
  const at = FUNNEL.indexOf(detail.stage as (typeof FUNNEL)[number]);
  const furthest = Math.max(at, ...Array.from(reachedAt.keys()).map((s) => FUNNEL.indexOf(s as (typeof FUNNEL)[number])));
  const screening = detail.screeningCalls.find((c) => c.score != null);

  const note = (stage: Stage): string | null => {
    if (stage === 'SCREENING' && screening) return `Score ${screening.score}/100 · ${screening.recommendation?.toLowerCase() ?? 'not rated'}`;
    const interview = detail.interviews.find((i) => i.round === (stage === 'INTERVIEW_1' ? 'AI_AVATAR' : 'ACADEMIC') && i.score != null);
    if ((stage === 'INTERVIEW_1' || stage === 'INTERVIEW_2') && interview) return `Score ${interview.score}/100${interview.result ? ` · ${interview.result.toLowerCase()}` : ''}`;
    return null;
  };

  return (
    <ol className="th-progress">
      <li className="th-progress__item th-progress__item--done">
        <span className="th-progress__dot" aria-hidden />
        <div>
          <div className="th-progress__title">Lead created</div>
          <div className="ds-text-subtle">{when(detail.createdAt)} · {SOURCE_LABELS[detail.source]}{detail.sourceDetail ? ` (${detail.sourceDetail})` : ''}</div>
        </div>
      </li>
      {PROGRESS.map(({ stage, label }) => {
        const i = FUNNEL.indexOf(stage as (typeof FUNNEL)[number]);
        const current = stage === detail.stage;
        const done = !current && i <= furthest && reachedAt.has(stage);
        const state = current ? 'current' : done ? 'done' : 'todo';
        const extra = note(stage);
        return (
          <li key={stage} className={`th-progress__item th-progress__item--${state}`}>
            <span className="th-progress__dot" aria-hidden />
            <div>
              <div className="th-progress__title">{label}</div>
              <div className="ds-text-subtle">
                {current
                  ? `${STAGE_STATUS_LABELS[detail.stageStatus]}${reachedAt.get(stage) ? ` · since ${when(reachedAt.get(stage))}` : ''}`
                  : done
                    ? when(reachedAt.get(stage))
                    : 'Pending'}
              </div>
              {extra && (state !== 'todo') && <div className="th-progress__note">{extra}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

interface Props {
  candidateId: string | null;
  initialTab?: DrawerTab;
  genres: Genre[];
  meta: HiringMeta | null;
  onClose: () => void;
  /** Something changed (stage, profile): refresh the list behind. */
  onChanged: () => void;
}

const CandidateDrawer: React.FC<Props> = ({ candidateId, initialTab = 'overview', genres, meta, onClose, onChanged }) => {
  const [detail, setDetail] = useState<CandidateDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<DrawerTab>(initialTab);
  const [moveTo, setMoveTo] = useState<Stage | null>(null);

  const load = useCallback(async () => {
    if (!candidateId) return;
    try {
      setDetail(await getCandidate(candidateId));
      setError(null);
    } catch (e) {
      setError(errorText(e));
    }
  }, [candidateId]);

  useEffect(() => {
    setDetail(null);
    setTab(initialTab);
    load();
  }, [candidateId, initialTab, load]);

  const changed = () => {
    load();
    onChanged();
  };

  const genreName = (id: string) => genres.find((g) => g.id === id)?.name ?? id;
  const action = detail ? nextAction(detail) : null;
  const run = (step: { tab?: DrawerTab; move?: Stage }) => {
    if (step.move) setMoveTo(step.move);
    else if (step.tab) setTab(step.tab);
  };

  return (
    <Drawer open={Boolean(candidateId)} onClose={onClose} label={detail ? `Candidate ${detail.name}` : 'Candidate'} size="lg">
      {error && <Callout tone="danger" title="Couldn't load this candidate">{error}</Callout>}
      {!detail && !error && <Loader label="Loading candidate…" />}
      {detail && (
        <div className="th-drawer">
          <header className="th-drawer__head">
            <span className="th-avatar th-avatar--lg" aria-hidden>{initials(detail.name)}</span>
            <div className="th-drawer__who">
              <h2 className="th-drawer__name">{detail.name}</h2>
              <div className="ds-text-subtle">
                {[detail.genres.map(genreName).join(', '), detail.city].filter(Boolean).join(' · ') || 'Genre not known'}
              </div>
              {detail.experienceYears != null && <div className="ds-text-subtle">{detail.experienceYears} years experience</div>}
              <div className="th-drawer__chips">
                <Badge tone="primary">{STAGE_LABELS[detail.stage]}</Badge>
                {detail.stageStatus !== 'PENDING' && <StatusBadge status={detail.stageStatus} label={STAGE_STATUS_LABELS[detail.stageStatus]} />}
                <CopyLinkButton candidateId={detail.id} name={detail.name} label />
                <Link className="ds-link th-drawer__full" to={`${process.env.PUBLIC_URL}/pages/teacher-hiring/${detail.id}`}>
                  Full profile <ExternalLink size={12} aria-hidden />
                </Link>
              </div>
            </div>
          </header>

          <div className="th-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`th-tabs__tab${tab === t.key ? ' th-tabs__tab--active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'overview' && (
            <div className="th-stack">
              <ProfileCard detail={detail} genres={genres} onChange={changed} />
              <Card title="Progress"><Progress detail={detail} /></Card>
              {action && (
                <Card title="Next action" className="th-next">
                  <div className="th-next__body">
                    <span className="th-next__icon" aria-hidden><Zap size={18} /></span>
                    <div>
                      <div className="th-next__title">{action.label}</div>
                      <p className="ds-text-subtle">{action.hint}</p>
                    </div>
                  </div>
                  <div className="th-next__actions">
                    {action.tab !== 'overview' && (
                      <Button onClick={() => run(action)} icon={<ArrowRight size={14} />}>{action.label}</Button>
                    )}
                    {action.secondary && (
                      <Button variant="secondary" onClick={() => run(action.secondary!)}>{action.secondary.label}</Button>
                    )}
                  </div>
                </Card>
              )}
            </div>
          )}
          {tab === 'call' && <ScreeningPanel detail={detail} onChange={changed} />}
          {tab === 'interview' && <AiInterviewPanel detail={detail} onChange={changed} />}
          {tab === 'academic' &&
            (detail.stage === 'INTERVIEW_2' || detail.interviews.some((i) => i.round === 'ACADEMIC') ? (
              <AcademicPanel
                key={detail.interviews.find((i) => i.round === 'ACADEMIC')?.id ?? 'new'}
                detail={detail}
                meta={meta}
                onChange={changed}
              />
            ) : (
              <Callout tone="info">The academic interview opens when the candidate moves to Interview 2.</Callout>
            ))}
          {tab === 'timeline' && (
            <div className="th-stack">
              <Timeline detail={detail} onChange={changed} />
              <Messages detail={detail} />
            </div>
          )}

          <MoveDialog
            candidate={moveTo ? { id: detail.id, name: detail.name, stage: detail.stage } : null}
            toStage={moveTo}
            onClose={() => setMoveTo(null)}
            onMoved={changed}
          />
        </div>
      )}
    </Drawer>
  );
};

export default CandidateDrawer;
